/**
 * UI Logic for Secret Santa Generator
 */

import {
  addPerson,
  createDefaultState,
  createInitialState,
  deserializeState,
  removePerson,
  serializeState,
  updateConstraint,
  updateSeed,
  addMetadataField,
  removeMetadataField,
  updateMetadata,
} from "./state.ts";
import { AppState, Constraint, SecretSantaResult } from "./types.ts";
import { formatResult } from "./algorithm.ts";
import type { WorkerRequest, WorkerResponse } from "./worker.ts";
import { downloadCsv, downloadJson } from "./csv.ts";

// Global state
let currentState: AppState = createDefaultState();
let worker: Worker | null = null;
let currentResult: SecretSantaResult | null = null;

// DOM elements
const nameInput = document.getElementById("nameInput") as HTMLInputElement;
const addButton = document.getElementById("addButton") as HTMLButtonElement;
const namesList = document.getElementById("namesList") as HTMLDivElement;
const metadataFieldsSection = document.getElementById("metadataFieldsSection") as HTMLElement;
const metadataFieldInput = document.getElementById("metadataFieldInput") as HTMLInputElement;
const addMetadataFieldButton = document.getElementById("addMetadataFieldButton") as HTMLButtonElement;
const metadataFieldsList = document.getElementById("metadataFieldsList") as HTMLDivElement;
const metadataTableSection = document.getElementById("metadataTableSection") as HTMLElement;
const metadataTable = document.getElementById("metadataTable") as HTMLTableElement;
const matrixSection = document.getElementById("matrixSection") as HTMLElement;
const constraintMatrix = document.getElementById("constraintMatrix") as HTMLTableElement;
const generationSection = document.getElementById("generationSection") as HTMLElement;
const seedInput = document.getElementById("seedInput") as HTMLInputElement;
const randomSeedButton = document.getElementById("randomSeedButton") as HTMLButtonElement;
const generateButton = document.getElementById("generateButton") as HTMLButtonElement;
const loadingIndicator = document.getElementById("loadingIndicator") as HTMLDivElement;
const resultSection = document.getElementById("resultSection") as HTMLDivElement;
const resultChain = document.getElementById("resultChain") as HTMLDivElement;
const resultDetails = document.getElementById("resultDetails") as HTMLDivElement;
const errorSection = document.getElementById("errorSection") as HTMLDivElement;
const errorMessage = document.getElementById("errorMessage") as HTMLParagraphElement;
const shareSection = document.getElementById("shareSection") as HTMLElement;
const copyUrlButton = document.getElementById("copyUrlButton") as HTMLButtonElement;
const downloadCsvButton = document.getElementById("downloadCsvButton") as HTMLButtonElement;
const downloadJsonButton = document.getElementById("downloadJsonButton") as HTMLButtonElement;

/**
 * Initialize the application
 */
function init(): void {
  // Load state from URL hash
  loadStateFromHash();

  // Set up event listeners
  addButton.addEventListener("click", handleAddPerson);
  nameInput.addEventListener("keypress", (e) => {
    if (e.key === "Enter") {
      handleAddPerson();
    }
  });
  addMetadataFieldButton.addEventListener("click", handleAddMetadataField);
  metadataFieldInput.addEventListener("keypress", (e) => {
    if (e.key === "Enter") {
      handleAddMetadataField();
    }
  });
  randomSeedButton.addEventListener("click", handleRandomSeed);
  generateButton.addEventListener("click", handleGenerate);
  copyUrlButton.addEventListener("click", handleCopyUrl);
  downloadCsvButton.addEventListener("click", handleDownloadCsv);
  downloadJsonButton.addEventListener("click", handleDownloadJson);

  // Update URL when seed changes
  seedInput.addEventListener("change", () => {
    const seed = parseInt(seedInput.value, 10);
    if (!isNaN(seed)) {
      currentState = updateSeed(currentState, seed);
      updateUrl();
    }
  });

  // Listen for hash changes (back/forward navigation)
  window.addEventListener("hashchange", loadStateFromHash);

  // Initial render
  render();
}

/**
 * Load state from URL hash
 */
function loadStateFromHash(): void {
  try {
    const hash = window.location.hash;
    if (hash) {
      currentState = deserializeState(hash);
    } else {
      currentState = createDefaultState();
    }
  } catch (error) {
    console.error("Failed to load state from hash:", error);
    currentState = createDefaultState();
  }
}

/**
 * Update URL hash with current state
 */
function updateUrl(): void {
  if (currentState.names.length > 0) {
    const hash = serializeState(currentState);
    window.location.hash = hash;
    shareSection.style.display = "block";
  } else {
    window.location.hash = "";
    shareSection.style.display = "none";
  }
}

/**
 * Handle adding a person
 */
function handleAddPerson(): void {
  const name = nameInput.value.trim();
  if (!name) return;

  // Check for duplicates
  if (currentState.names.includes(name)) {
    alert("This name already exists!");
    return;
  }

  currentState = addPerson(currentState, name);
  nameInput.value = "";
  updateUrl();
  render();
}

/**
 * Handle removing a person
 */
function handleRemovePerson(index: number): void {
  currentState = removePerson(currentState, index);
  updateUrl();
  render();
}

/**
 * Handle adding a metadata field
 */
function handleAddMetadataField(): void {
  const fieldName = metadataFieldInput.value.trim();
  if (!fieldName) return;

  // Check for duplicates
  if (currentState.metadataFields.includes(fieldName)) {
    alert("This field already exists!");
    return;
  }

  currentState = addMetadataField(currentState, fieldName);
  metadataFieldInput.value = "";
  updateUrl();
  render();
}

/**
 * Handle removing a metadata field
 */
function handleRemoveMetadataField(index: number): void {
  currentState = removeMetadataField(currentState, index);
  updateUrl();
  render();
}

/**
 * Handle updating metadata value
 */
function handleUpdateMetadata(personIndex: number, fieldIndex: number, value: string): void {
  currentState = updateMetadata(currentState, personIndex, fieldIndex, value);
  updateUrl();
}

/**
 * Handle toggling a constraint
 */
function handleToggleConstraint(from: number, to: number): void {
  if (from === to) return; // Can't change self-constraint

  const current = currentState.constraints[from][to];
  let next: Constraint;

  // Cycle: CAN -> MUST -> CANNOT -> CAN
  if (current === Constraint.CAN) {
    next = Constraint.MUST;
  } else if (current === Constraint.MUST) {
    next = Constraint.CANNOT;
  } else {
    next = Constraint.CAN;
  }

  currentState = updateConstraint(currentState, from, to, next);
  updateUrl();
  render();
}

/**
 * Handle random seed generation
 */
function handleRandomSeed(): void {
  const seed = Math.floor(Math.random() * 1000000);
  seedInput.value = seed.toString();
  currentState = updateSeed(currentState, seed);
  updateUrl();
}

/**
 * Handle Secret Santa generation
 */
function handleGenerate(): void {
  // Hide previous results/errors
  resultSection.style.display = "none";
  errorSection.style.display = "none";
  loadingIndicator.style.display = "block";
  generateButton.disabled = true;

  // Update seed from input if provided
  const seedValue = seedInput.value.trim();
  if (seedValue) {
    const seed = parseInt(seedValue, 10);
    if (!isNaN(seed)) {
      currentState = updateSeed(currentState, seed);
    }
  } else {
    // Generate random seed if not provided
    currentState = updateSeed(currentState, Date.now());
    seedInput.value = currentState.seed.toString();
  }
  updateUrl();

  // Create worker if needed
  if (!worker) {
    worker = new Worker("worker.js", { type: "module" });
    worker.onmessage = handleWorkerMessage;
  }

  // Send request to worker
  const request: WorkerRequest = {
    type: "generate",
    state: currentState,
  };
  worker.postMessage(request);
}

/**
 * Handle worker message
 */
function handleWorkerMessage(event: MessageEvent<WorkerResponse>): void {
  loadingIndicator.style.display = "none";
  generateButton.disabled = false;

  const response = event.data;

  if (response.type === "success" && response.result) {
    // Store the result for CSV download
    currentResult = response.result;

    // Display result - just the chain
    resultChain.textContent = formatResult(currentState, response.result);

    // Hide the details section (not needed anymore)
    resultDetails.style.display = "none";

    resultSection.style.display = "block";
    errorSection.style.display = "none";
  } else if (response.type === "error") {
    errorMessage.textContent = response.error || "Unknown error occurred";
    errorSection.style.display = "block";
    resultSection.style.display = "none";
    currentResult = null;
  }
}

/**
 * Handle CSV download
 */
function handleDownloadCsv(): void {
  if (!currentResult) {
    return;
  }

  downloadCsv(currentState, currentResult);

  // Visual feedback
  const originalText = downloadCsvButton.textContent;
  downloadCsvButton.textContent = "Downloaded!";
  setTimeout(() => {
    downloadCsvButton.textContent = originalText;
  }, 2000);
}

/**
 * Handle JSON download
 */
function handleDownloadJson(): void {
  if (!currentResult) {
    return;
  }

  downloadJson(currentState, currentResult);

  // Visual feedback
  const originalText = downloadJsonButton.textContent;
  downloadJsonButton.textContent = "Downloaded!";
  setTimeout(() => {
    downloadJsonButton.textContent = originalText;
  }, 2000);
}

/**
 * Handle copy URL
 */
function handleCopyUrl(): void {
  const url = window.location.href;
  navigator.clipboard.writeText(url).then(() => {
    const originalText = copyUrlButton.textContent;
    copyUrlButton.textContent = "Copied!";
    setTimeout(() => {
      copyUrlButton.textContent = originalText;
    }, 2000);
  });
}

/**
 * Render the UI
 */
function render(): void {
  renderNamesList();
  renderMetadataFields();
  renderMetadataTable();
  renderMatrix();
  renderSeed();
  updateVisibility();
}

/**
 * Render names list
 */
function renderNamesList(): void {
  namesList.innerHTML = "";

  currentState.names.forEach((name, index) => {
    const tag = document.createElement("div");
    tag.className = "name-tag";

    const nameSpan = document.createElement("span");
    nameSpan.textContent = name;

    const removeBtn = document.createElement("button");
    removeBtn.textContent = "×";
    removeBtn.setAttribute("aria-label", `Remove ${name}`);
    removeBtn.addEventListener("click", () => handleRemovePerson(index));

    tag.appendChild(nameSpan);
    tag.appendChild(removeBtn);
    namesList.appendChild(tag);
  });
}

/**
 * Render metadata fields list
 */
function renderMetadataFields(): void {
  metadataFieldsList.innerHTML = "";

  currentState.metadataFields.forEach((fieldName, index) => {
    const tag = document.createElement("div");
    tag.className = "name-tag";

    const fieldSpan = document.createElement("span");
    fieldSpan.textContent = fieldName;

    const removeBtn = document.createElement("button");
    removeBtn.textContent = "×";
    removeBtn.setAttribute("aria-label", `Remove ${fieldName}`);
    removeBtn.addEventListener("click", () => handleRemoveMetadataField(index));

    tag.appendChild(fieldSpan);
    tag.appendChild(removeBtn);
    metadataFieldsList.appendChild(tag);
  });
}

/**
 * Render metadata table
 */
function renderMetadataTable(): void {
  if (currentState.names.length === 0 || currentState.metadataFields.length === 0) {
    metadataTable.innerHTML = "";
    return;
  }

  // Build table
  let html = "<thead><tr><th>Name</th>";

  // Header row with field names
  for (const fieldName of currentState.metadataFields) {
    html += `<th>${fieldName}</th>`;
  }
  html += "</tr></thead><tbody>";

  // Data rows
  for (let i = 0; i < currentState.names.length; i++) {
    html += `<tr><th>${currentState.names[i]}</th>`;

    for (let j = 0; j < currentState.metadataFields.length; j++) {
      const value = currentState.metadata[i]?.[j] || "";
      html += `<td><input type="text" value="${value}" data-person="${i}" data-field="${j}" /></td>`;
    }

    html += "</tr>";
  }

  html += "</tbody>";
  metadataTable.innerHTML = html;

  // Add input listeners
  const inputs = metadataTable.querySelectorAll("input");
  inputs.forEach((input) => {
    input.addEventListener("input", (e) => {
      const target = e.target as HTMLInputElement;
      const personIndex = parseInt(target.getAttribute("data-person")!);
      const fieldIndex = parseInt(target.getAttribute("data-field")!);
      handleUpdateMetadata(personIndex, fieldIndex, target.value);
    });
  });
}

/**
 * Render constraint matrix
 */
function renderMatrix(): void {
  const n = currentState.names.length;

  if (n === 0) {
    constraintMatrix.innerHTML = "";
    return;
  }

  // Build table
  let html = "<thead><tr><th></th>";

  // Header row with recipient names
  for (let j = 0; j < n; j++) {
    html += `<th title="Receives from">${currentState.names[j]}</th>`;
  }
  html += "</tr></thead><tbody>";

  // Data rows
  for (let i = 0; i < n; i++) {
    html += `<tr><th title="Gives to">${currentState.names[i]}</th>`;

    for (let j = 0; j < n; j++) {
      const constraint = currentState.constraints[i][j];
      const isDisabled = i === j;

      let className = "";
      let text = "";

      if (isDisabled) {
        className = "disabled";
        text = "—";
      } else if (constraint === Constraint.CAN) {
        className = "can";
        text = "Can";
      } else if (constraint === Constraint.MUST) {
        className = "must";
        text = "Must";
      } else {
        className = "cannot";
        text = "Cannot";
      }

      html += `<td class="${className}" data-from="${i}" data-to="${j}">${text}</td>`;
    }

    html += "</tr>";
  }

  html += "</tbody>";
  constraintMatrix.innerHTML = html;

  // Add click listeners
  const cells = constraintMatrix.querySelectorAll("td:not(.disabled)");
  cells.forEach((cell) => {
    cell.addEventListener("click", () => {
      const from = parseInt(cell.getAttribute("data-from")!);
      const to = parseInt(cell.getAttribute("data-to")!);
      handleToggleConstraint(from, to);
    });
  });
}

/**
 * Render seed input
 */
function renderSeed(): void {
  if (currentState.seed && !seedInput.value) {
    seedInput.value = currentState.seed.toString();
  }
}

/**
 * Update section visibility
 */
function updateVisibility(): void {
  const hasNames = currentState.names.length > 0;
  const hasEnoughPeople = currentState.names.length >= 2;
  const hasMetadataFields = currentState.metadataFields.length > 0;

  metadataFieldsSection.style.display = hasNames ? "block" : "none";
  metadataTableSection.style.display = hasNames && hasMetadataFields ? "block" : "none";
  matrixSection.style.display = hasNames ? "block" : "none";
  generationSection.style.display = hasEnoughPeople ? "block" : "none";
}

// Initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
